// Canonical club catalogue. Order = bag order (longest → shortest).
export const CLUB_CATALOG = [
  { key: "driver", label: "Driver",   default: 230 },
  { key: "3wood",  label: "3-wood",   default: 215 },
  { key: "5wood",  label: "5-wood",   default: 200 },
  { key: "hybrid", label: "Hybrid",   default: 190 },
  { key: "3iron",  label: "3-iron",   default: 185 },
  { key: "4iron",  label: "4-iron",   default: 175 },
  { key: "5iron",  label: "5-iron",   default: 165 },
  { key: "6iron",  label: "6-iron",   default: 155 },
  { key: "7iron",  label: "7-iron",   default: 145 },
  { key: "8iron",  label: "8-iron",   default: 135 },
  { key: "9iron",  label: "9-iron",   default: 120 },
  { key: "pw",     label: "PW",       default: 105 },
  { key: "gw",     label: "GW",       default: 95  },
  { key: "sw",     label: "SW",       default: 80  },
  { key: "lw",     label: "LW",       default: 60  },
];

// Rough thumb-rules for wind. We compute an "effective" distance (the yardage
// to hit your normal swing against the wind):
//   pure headwind: +1.0 yd per 1 mph of wind
//   pure tailwind: -0.6 yd per 1 mph of wind
//   pure crosswind: ~+0.1 yd per 1 mph (very small distance effect; crosswind
//                   mostly affects aim, not distance)
// `windRelDeg` is the wind direction RELATIVE to the shot — 0° = headwind,
// 180° = tailwind, 90°/270° = crosswind. We project onto the head/tail axis.
export function adjustDistanceForWind(distanceYds, windMph, windRelDeg) {
  if (distanceYds == null || windMph == null || windRelDeg == null) return distanceYds;
  const rad = (windRelDeg * Math.PI) / 180;
  const headComponent = Math.cos(rad); // +1 = pure head, -1 = pure tail
  const crossComponent = Math.abs(Math.sin(rad));
  let adj = 0;
  if (headComponent > 0) adj += headComponent * windMph * 1.0;
  if (headComponent < 0) adj += headComponent * windMph * 0.6; // tailwind shortens carry
  adj += crossComponent * windMph * 0.1;
  return distanceYds + adj;
}

/**
 * Suggest a club for a given carry distance.
 * Returns { club: {key,label,yards}, delta } where delta is yards over/under
 * the user's average for that club (positive = need to hit it harder).
 * Returns null when the user hasn't configured any clubs.
 *
 * Algorithm: pick the club whose yardage is the SMALLEST value >= target.
 * If every club is shorter than the target → return the longest club with
 * the (positive) gap. If every club is longer → return the shortest.
 */
export function suggestClub(yardages, targetYds) {
  if (!yardages || targetYds == null) return null;
  const entries = Object.entries(yardages)
    .filter(([, v]) => typeof v === "number" && v > 0)
    .map(([key, yards]) => {
      const meta = CLUB_CATALOG.find((c) => c.key === key);
      return meta ? { key, label: meta.label, yards } : null;
    })
    .filter(Boolean);
  if (entries.length === 0) return null;
  entries.sort((a, b) => a.yards - b.yards);

  // First club whose yardage covers the target
  const cover = entries.find((c) => c.yards >= targetYds);
  if (cover) {
    return { club: cover, delta: Math.round(targetYds - cover.yards) };
  }
  // Everything is too short — pick the longest, report the shortfall
  const longest = entries[entries.length - 1];
  return { club: longest, delta: Math.round(targetYds - longest.yards) };
}
